import { useState, useRef, useCallback } from "react";
import { C } from "../constants/colors";

/* ═══════════════════════════════════════════════════════════════
   SAMAJH — Interactive Diagrams
   Drag-to-explore educational SVG components
   ═══════════════════════════════════════════════════════════════ */

const ff = "'Outfit',sans-serif";
const clamp = (v,lo,hi) => Math.max(lo,Math.min(hi,v));

// Shared hook for drag on SVG
function useDrag(svgRef, vbWidth, vbHeight, onDrag) {
  const dragging = useRef(false);
  const getSvgCoords = useCallback((e) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * vbWidth,
      y: ((clientY - rect.top) / rect.height) * vbHeight,
    };
  }, [svgRef, vbWidth, vbHeight]);

  const onStart = useCallback((e) => { e.preventDefault(); dragging.current = true; }, []);
  const onMove = useCallback((e) => {
    if (!dragging.current) return;
    e.preventDefault();
    onDrag(getSvgCoords(e));
  }, [onDrag, getSvgCoords]);
  const onEnd = useCallback(() => { dragging.current = false; }, []);

  return { onStart, onMove, onEnd };
}

// ═══════════════════════════════════════
//  1. ANGLE EXPLORER
// ═══════════════════════════════════════

export function InteractiveAngleExplorer() {
  const svgRef = useRef(null);
  const [angle, setAngle] = useState(45);

  const handleDrag = useCallback(({x, y}) => {
    const a = Math.atan2(-(y - 160), x - 80) * (180 / Math.PI);
    setAngle(clamp(Math.round(a), 1, 179));
  }, []);

  const { onStart, onMove, onEnd } = useDrag(svgRef, 400, 220, handleDrag);
  const rad = (angle * Math.PI) / 180;
  const ex = 80 + 140 * Math.cos(rad);
  const ey = 160 - 140 * Math.sin(rad);
  const arcR = 35;
  const ax = 80 + arcR * Math.cos(rad);
  const ay = 160 - arcR * Math.sin(rad);
  const largeArc = angle > 180 ? 1 : 0;
  const type = angle < 90 ? "Acute" : angle === 90 ? "Right" : "Obtuse";
  const col = angle < 90 ? C.primaryMid : angle === 90 ? "#E24B4A" : C.amberDark;

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 220" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd} onTouchMove={onMove} onTouchEnd={onEnd}>
        <line x1="80" y1="160" x2="320" y2="160" stroke={C.textMid} strokeWidth="2"/>
        <line x1="80" y1="160" x2={ex} y2={ey} stroke={col} strokeWidth="2.5"/>
        <path d={`M ${80+arcR} 160 A ${arcR} ${arcR} 0 ${largeArc} 0 ${ax} ${ay}`} fill="none" stroke={col} strokeWidth="2"/>
        {angle === 90 && <rect x="80" y="144" width="16" height="16" fill="none" stroke={col} strokeWidth="1.5"/>}
        <circle cx={ex} cy={ey} r="10" fill={col} stroke="white" strokeWidth="2" style={{cursor:"grab"}}
          onMouseDown={onStart} onTouchStart={onStart}/>
        <circle cx="80" cy="160" r="4" fill={C.text}/>
        <text x={80 + arcR + 14} y={160 - 12} fontSize="14" fill={col} fontFamily={ff} fontWeight="500">{angle}°</text>
        <text x="200" y="210" textAnchor="middle" fontSize="14" fill={col} fontFamily={ff} fontWeight="500">{type} angle</text>
        <text x="200" y="18" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag the point to change the angle</text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  2. PYTHAGORAS THEOREM
// ═══════════════════════════════════════

export function InteractivePythagoras() {
  const svgRef = useRef(null);
  const [bLen, setBLen] = useState(120);

  const handleDrag = useCallback(({x}) => {
    setBLen(clamp(Math.round(x - 80), 40, 220));
  }, []);

  const { onStart, onMove, onEnd } = useDrag(svgRef, 400, 260, handleDrag);
  const aLen = 100;
  const c = Math.sqrt(aLen * aLen + bLen * bLen);
  const a2 = Math.round((aLen / 30) * (aLen / 30) * 10) / 10;
  const b2 = Math.round((bLen / 30) * (bLen / 30) * 10) / 10;
  const c2 = Math.round(a2 + b2, 1);
  const aVal = (aLen / 30).toFixed(1);
  const bVal = (bLen / 30).toFixed(1);
  const cVal = Math.sqrt(a2 + b2).toFixed(1);

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 260" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd} onTouchMove={onMove} onTouchEnd={onEnd}>
        <text x="200" y="18" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag the bottom-right corner</text>
        <polygon points={`80,180 80,${180-aLen} ${80+bLen},180`} fill="#E1F5EE" stroke={C.primaryMid} strokeWidth="2.5"/>
        <rect x="80" y={180-16} width="16" height="16" fill="none" stroke={C.amberDark} strokeWidth="1.5"/>
        <circle cx={80+bLen} cy="180" r="10" fill={C.primaryMid} stroke="white" strokeWidth="2" style={{cursor:"ew-resize"}}
          onMouseDown={onStart} onTouchStart={onStart}/>
        <text x="65" y={180-aLen/2+5} fontSize="12" fill={C.primaryMid} fontFamily={ff} fontWeight="500" textAnchor="end">a = {aVal}</text>
        <text x={80+bLen/2} y="198" fontSize="12" fill={C.primaryMid} fontFamily={ff} fontWeight="500" textAnchor="middle">b = {bVal}</text>
        <text x={80+bLen/2+10} y={180-aLen/2-5} fontSize="12" fill="#E24B4A" fontFamily={ff} fontWeight="500" textAnchor="middle">c = {cVal}</text>
        <rect x="80" y="215" width="240" height="36" rx="10" fill="#FAEEDA"/>
        <text x="200" y="234" textAnchor="middle" fontSize="13" fill={C.amberDark} fontFamily={ff} fontWeight="500">
          {aVal}² + {bVal}² = {a2.toFixed(1)} + {b2.toFixed(1)} = {c2.toFixed(1)} = {cVal}²
        </text>
        <text x="200" y="248" textAnchor="middle" fontSize="11" fill={C.textMute} fontFamily={ff}>a² + b² = c² ✓</text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  3. TRIG RATIOS
// ═══════════════════════════════════════

export function InteractiveTrigRatios() {
  const svgRef = useRef(null);
  const [angle, setAngle] = useState(45);

  const handleDrag = useCallback(({x, y}) => {
    const a = Math.atan2(-(y - 200), x - 60) * (180 / Math.PI);
    setAngle(clamp(Math.round(a), 5, 85));
  }, []);

  const { onStart, onMove, onEnd } = useDrag(svgRef, 400, 280, handleDrag);
  const rad = (angle * Math.PI) / 180;
  const hyp = 150;
  const adj = hyp * Math.cos(rad);
  const opp = hyp * Math.sin(rad);
  const px = 60 + adj;
  const py = 200 - opp;
  const sinV = Math.sin(rad).toFixed(3);
  const cosV = Math.cos(rad).toFixed(3);
  const tanV = angle >= 85 ? "∞" : Math.tan(rad).toFixed(3);

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 280" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd} onTouchMove={onMove} onTouchEnd={onEnd}>
        <text x="200" y="18" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag the point to change angle θ</text>
        <polygon points={`60,200 ${px},200 ${px},${py}`} fill="#E1F5EE" stroke={C.primaryMid} strokeWidth="2"/>
        <line x1="60" y1="200" x2={px} y2={py} stroke="#E24B4A" strokeWidth="2.5"/>
        <rect x={px-14} y={200-14} width="14" height="14" fill="none" stroke={C.amberDark} strokeWidth="1.5"/>
        <circle cx={px} cy={py} r="10" fill="#E24B4A" stroke="white" strokeWidth="2" style={{cursor:"grab"}}
          onMouseDown={onStart} onTouchStart={onStart}/>
        <path d={`M ${60+30} 200 A 30 30 0 0 0 ${60+30*Math.cos(rad)} ${200-30*Math.sin(rad)}`} fill="none" stroke={C.amberDark} strokeWidth="2"/>
        <text x={60+40} y={192} fontSize="14" fill={C.amberDark} fontFamily={ff} fontWeight="500">θ = {angle}°</text>
        <text x={px+8} y={200-(opp/2)+5} fontSize="11" fill="#E24B4A" fontFamily={ff} fontWeight="500">Opp</text>
        <text x={60+(adj/2)} y="218" fontSize="11" fill={C.primaryMid} fontFamily={ff} fontWeight="500" textAnchor="middle">Adj</text>
        <text x={(60+px)/2-15} y={(200+py)/2-8} fontSize="11" fill="#E24B4A" fontFamily={ff} fontWeight="500">Hyp</text>
        <rect x="60" y="235" width="100" height="38" rx="8" fill="#E1F5EE"/>
        <text x="110" y="252" textAnchor="middle" fontSize="12" fill={C.primaryMid} fontFamily={ff} fontWeight="500">sin θ = {sinV}</text>
        <text x="110" y="266" textAnchor="middle" fontSize="10" fill={C.textMute} fontFamily={ff}>Opp / Hyp</text>
        <rect x="170" y="235" width="100" height="38" rx="8" fill="#FAEEDA"/>
        <text x="220" y="252" textAnchor="middle" fontSize="12" fill={C.amberDark} fontFamily={ff} fontWeight="500">cos θ = {cosV}</text>
        <text x="220" y="266" textAnchor="middle" fontSize="10" fill={C.textMute} fontFamily={ff}>Adj / Hyp</text>
        <rect x="280" y="235" width="100" height="38" rx="8" fill="#EEEDFE"/>
        <text x="330" y="252" textAnchor="middle" fontSize="12" fill={C.purpleMid} fontFamily={ff} fontWeight="500">tan θ = {tanV}</text>
        <text x="330" y="266" textAnchor="middle" fontSize="10" fill={C.textMute} fontFamily={ff}>Opp / Adj</text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  4. REFLECTION LAW
// ═══════════════════════════════════════

export function InteractiveReflection() {
  const svgRef = useRef(null);
  const [incAngle, setIncAngle] = useState(45);

  const handleDrag = useCallback(({x, y}) => {
    const a = Math.atan2(-(y - 170), -(x - 200)) * (180 / Math.PI);
    setIncAngle(clamp(Math.round(a), 10, 80));
  }, []);

  const { onStart, onMove, onEnd } = useDrag(svgRef, 400, 230, handleDrag);
  const rad = (incAngle * Math.PI) / 180;
  const rayLen = 120;
  const ix = 200 - rayLen * Math.sin(rad);
  const iy = 170 - rayLen * Math.cos(rad);
  const rx = 200 + rayLen * Math.sin(rad);
  const ry = 170 - rayLen * Math.cos(rad);

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 230" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd} onTouchMove={onMove} onTouchEnd={onEnd}>
        <text x="200" y="16" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag the yellow dot to change incident angle</text>
        <line x1="60" y1="170" x2="340" y2="170" stroke={C.textMid} strokeWidth="3"/>
        {[70,90,110,130,150,170,190,210,230,250,270,290,310,330].map(x =>
          <line key={x} x1={x} y1="170" x2={x-8} y2="182" stroke={C.textMute} strokeWidth="1"/>
        )}
        <line x1="200" y1="170" x2="200" y2="30" stroke={C.textMid} strokeWidth="1" strokeDasharray="5,3"/>
        <text x="208" y="38" fontSize="10" fill={C.textMid} fontFamily={ff}>Normal</text>
        <line x1={ix} y1={iy} x2="200" y2="170" stroke={C.amberDark} strokeWidth="2.5"/>
        <line x1="200" y1="170" x2={rx} y2={ry} stroke="#E24B4A" strokeWidth="2.5"/>
        <circle cx={ix} cy={iy} r="10" fill={C.amberDark} stroke="white" strokeWidth="2" style={{cursor:"grab"}}
          onMouseDown={onStart} onTouchStart={onStart}/>
        <circle cx="200" cy="170" r="4" fill={C.text}/>
        <path d={`M 200 ${170-30} A 30 30 0 0 0 ${200-30*Math.sin(rad)} ${170-30*Math.cos(rad)}`} fill="none" stroke={C.amberDark} strokeWidth="2"/>
        <path d={`M 200 ${170-30} A 30 30 0 0 1 ${200+30*Math.sin(rad)} ${170-30*Math.cos(rad)}`} fill="none" stroke="#E24B4A" strokeWidth="2"/>
        <text x={185-20*Math.sin(rad)} y={155-20*Math.cos(rad)} fontSize="13" fill={C.amberDark} fontFamily={ff} fontWeight="500">i={incAngle}°</text>
        <text x={215+15*Math.sin(rad)} y={155-20*Math.cos(rad)} fontSize="13" fill="#E24B4A" fontFamily={ff} fontWeight="500">r={incAngle}°</text>
        <rect x="120" y="195" width="160" height="28" rx="10" fill="#E1F5EE"/>
        <text x="200" y="213" textAnchor="middle" fontSize="13" fill={C.primaryMid} fontFamily={ff} fontWeight="500">∠i = ∠r = {incAngle}° ✓</text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  5. REFRACTION
// ═══════════════════════════════════════

export function InteractiveRefraction() {
  const svgRef = useRef(null);
  const [incAngle, setIncAngle] = useState(45);

  const handleDrag = useCallback(({x, y}) => {
    const a = Math.atan2(-(y - 130), -(x - 200)) * (180 / Math.PI);
    setIncAngle(clamp(Math.round(a), 5, 80));
  }, []);

  const { onStart, onMove, onEnd } = useDrag(svgRef, 400, 280, handleDrag);
  const n1 = 1.0, n2 = 1.5;
  const iRad = (incAngle * Math.PI) / 180;
  const sinR = (n1 * Math.sin(iRad)) / n2;
  const refAngle = Math.round(Math.asin(clamp(sinR, 0, 0.99)) * (180 / Math.PI));
  const rRad = (refAngle * Math.PI) / 180;
  const rayLen = 90;
  const ix = 200 - rayLen * Math.sin(iRad);
  const iy = 130 - rayLen * Math.cos(iRad);
  const rx2 = 200 + rayLen * Math.sin(rRad);
  const ry2 = 130 + rayLen * Math.cos(rRad);

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 280" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd} onTouchMove={onMove} onTouchEnd={onEnd}>
        <text x="200" y="16" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag the dot to change incident angle</text>
        <rect x="40" y="25" width="320" height="105" fill="#E6F1FB" rx="4"/>
        <text x="55" y="45" fontSize="10" fill="#185FA5" fontFamily={ff} fontWeight="500">Air (n=1.0)</text>
        <rect x="40" y="130" width="320" height="110" fill="#E1F5EE" rx="4"/>
        <text x="55" y="230" fontSize="10" fill={C.primaryMid} fontFamily={ff} fontWeight="500">Water (n=1.5)</text>
        <line x1="40" y1="130" x2="360" y2="130" stroke={C.textMid} strokeWidth="2"/>
        <line x1="200" y1="30" x2="200" y2="235" stroke={C.textMid} strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={ix} y1={iy} x2="200" y2="130" stroke={C.amberDark} strokeWidth="2.5"/>
        <line x1="200" y1="130" x2={rx2} y2={ry2} stroke="#E24B4A" strokeWidth="2.5"/>
        <circle cx={ix} cy={iy} r="10" fill={C.amberDark} stroke="white" strokeWidth="2" style={{cursor:"grab"}}
          onMouseDown={onStart} onTouchStart={onStart}/>
        <circle cx="200" cy="130" r="4" fill={C.text}/>
        <text x={178} y={110} fontSize="12" fill={C.amberDark} fontFamily={ff} fontWeight="500">∠i = {incAngle}°</text>
        <text x={215} y={160} fontSize="12" fill="#E24B4A" fontFamily={ff} fontWeight="500">∠r = {refAngle}°</text>
        <rect x="80" y="248" width="240" height="28" rx="10" fill="#FAEEDA"/>
        <text x="200" y="266" textAnchor="middle" fontSize="12" fill={C.amberDark} fontFamily={ff} fontWeight="500">
          n₁ sin(i) = n₂ sin(r) → 1.0 × sin({incAngle}°) = 1.5 × sin({refAngle}°)
        </text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  6. CIRCLE TANGENT
// ═══════════════════════════════════════

export function InteractiveCircleTangent() {
  const svgRef = useRef(null);
  const [angle, setAngle] = useState(0);

  const handleDrag = useCallback(({x, y}) => {
    setAngle(Math.atan2(y - 140, x - 200));
  }, []);

  const { onStart, onMove, onEnd } = useDrag(svgRef, 400, 280, handleDrag);
  const cx = 200, cy = 140, r = 80;
  const px = cx + r * Math.cos(angle);
  const py = cy + r * Math.sin(angle);
  const tx = -Math.sin(angle), ty = Math.cos(angle);
  const tLen = 70;
  const sq = 12;
  const nx = Math.cos(angle), ny = Math.sin(angle);
  const s1x = px - nx * sq, s1y = py - ny * sq;
  const s2x = s1x + tx * sq, s2y = s1y + ty * sq;
  const s3x = px + tx * sq, s3y = py + ty * sq;

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 280" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd} onTouchMove={onMove} onTouchEnd={onEnd}>
        <text x="200" y="18" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag point P around the circle</text>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#9FE1CB" strokeWidth="2"/>
        <circle cx={cx} cy={cy} r="4" fill={C.primaryMid}/>
        <text x={cx} y={cy-10} textAnchor="middle" fontSize="11" fill={C.text} fontFamily={ff} fontWeight="500">O</text>
        <line x1={cx} y1={cy} x2={px} y2={py} stroke={C.primaryMid} strokeWidth="2"/>
        <line x1={px - tx*tLen} y1={py - ty*tLen} x2={px + tx*tLen} y2={py + ty*tLen} stroke={C.amberDark} strokeWidth="2.5"/>
        <path d={`M ${s1x} ${s1y} L ${s2x} ${s2y} L ${s3x} ${s3y}`} fill="none" stroke="#E24B4A" strokeWidth="1.5"/>
        <circle cx={px} cy={py} r="10" fill={C.amberDark} stroke="white" strokeWidth="2" style={{cursor:"grab"}}
          onMouseDown={onStart} onTouchStart={onStart}/>
        <text x={px + 16*nx} y={py + 16*ny + 4} fontSize="11" fill={C.text} fontFamily={ff} fontWeight="500">P</text>
        <rect x="110" y="240" width="180" height="30" rx="10" fill="#E1F5EE"/>
        <text x="200" y="259" textAnchor="middle" fontSize="12" fill={C.primaryMid} fontFamily={ff} fontWeight="500">OP ⊥ tangent = 90° always</text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  7. COORDINATE PLANE EXPLORER
// ═══════════════════════════════════════

export function InteractiveCoordinatePlane() {
  const svgRef = useRef(null);
  const [pt, setPt] = useState({ x: 3, y: 2 });

  const handleDrag = useCallback(({x, y}) => {
    const gx = Math.round((x - 200) / 30);
    const gy = Math.round(-(y - 150) / 30);
    setPt({ x: clamp(gx, -5, 5), y: clamp(gy, -4, 4) });
  }, []);

  const { onStart, onMove, onEnd } = useDrag(svgRef, 400, 300, handleDrag);
  const sx = 200 + pt.x * 30;
  const sy = 150 - pt.y * 30;
  const quadrant = pt.x > 0 && pt.y > 0 ? "I" : pt.x < 0 && pt.y > 0 ? "II" : pt.x < 0 && pt.y < 0 ? "III" : pt.x > 0 && pt.y < 0 ? "IV" : "axis";

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 300" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd} onTouchMove={onMove} onTouchEnd={onEnd}>
        <text x="200" y="16" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag the point to explore coordinates</text>
        {/* Grid */}
        {[-5,-4,-3,-2,-1,0,1,2,3,4,5].map(i => <line key={`v${i}`} x1={200+i*30} y1="30" x2={200+i*30} y2="270" stroke="#f1f5f9" strokeWidth="1"/>)}
        {[-4,-3,-2,-1,0,1,2,3,4].map(i => <line key={`h${i}`} x1="50" y1={150+i*30} x2="350" y2={150+i*30} stroke="#f1f5f9" strokeWidth="1"/>)}
        <line x1="50" y1="150" x2="355" y2="150" stroke={C.textMid} strokeWidth="1.5"/>
        <line x1="200" y1="270" x2="200" y2="25" stroke={C.textMid} strokeWidth="1.5"/>
        <text x="360" y="154" fontSize="11" fill={C.textMid} fontFamily={ff}>x</text>
        <text x="204" y="24" fontSize="11" fill={C.textMid} fontFamily={ff}>y</text>
        {[-5,-4,-3,-2,-1,1,2,3,4,5].map(n => <text key={`xl${n}`} x={200+n*30} y="164" fontSize="8" fill={C.textMute} fontFamily={ff} textAnchor="middle">{n}</text>)}
        {[-4,-3,-2,-1,1,2,3,4].map(n => <text key={`yl${n}`} x="192" y={150-n*30+4} fontSize="8" fill={C.textMute} fontFamily={ff} textAnchor="end">{n}</text>)}
        {/* Dashed lines to axes */}
        <line x1={sx} y1="150" x2={sx} y2={sy} stroke={C.primaryMid} strokeWidth="1" strokeDasharray="4,3"/>
        <line x1="200" y1={sy} x2={sx} y2={sy} stroke={C.primaryMid} strokeWidth="1" strokeDasharray="4,3"/>
        {/* Point */}
        <circle cx={sx} cy={sy} r="10" fill={C.primaryMid} stroke="white" strokeWidth="2" style={{cursor:"grab"}}
          onMouseDown={onStart} onTouchStart={onStart}/>
        <text x={sx+14} y={sy-8} fontSize="12" fill={C.primaryMid} fontFamily={ff} fontWeight="500">({pt.x}, {pt.y})</text>
        {/* Info */}
        <rect x="110" y="275" width="180" height="22" rx="8" fill="#E1F5EE"/>
        <text x="200" y="290" textAnchor="middle" fontSize="11" fill={C.primaryMid} fontFamily={ff} fontWeight="500">
          Quadrant {quadrant} • ({pt.x >= 0 ? "+" : "−"}, {pt.y >= 0 ? "+" : "−"})
        </text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  8. DISTANCE FORMULA
// ═══════════════════════════════════════

export function InteractiveDistanceFormula() {
  const svgRef = useRef(null);
  const [p1, setP1] = useState({ x: 1, y: 1 });
  const [p2, setP2] = useState({ x: 4, y: 5 });
  const [dragTarget, setDragTarget] = useState(null);

  const handleDrag = useCallback(({x, y}) => {
    const gx = Math.round((x - 80) / 30);
    const gy = Math.round(-(y - 200) / 30);
    const pt = { x: clamp(gx, 0, 8), y: clamp(gy, 0, 7) };
    if (dragTarget === 'p1') setP1(pt);
    else if (dragTarget === 'p2') setP2(pt);
  }, [dragTarget]);

  const { onMove, onEnd } = useDrag(svgRef, 400, 270, handleDrag);
  const onStartP1 = useCallback((e) => { e.preventDefault(); setDragTarget('p1'); }, []);
  const onStartP2 = useCallback((e) => { e.preventDefault(); setDragTarget('p2'); }, []);
  const handleEnd = useCallback(() => { setDragTarget(null); onEnd(); }, [onEnd]);

  const s1x = 80 + p1.x * 30, s1y = 200 - p1.y * 30;
  const s2x = 80 + p2.x * 30, s2y = 200 - p2.y * 30;
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const dist = Math.sqrt(dx * dx + dy * dy).toFixed(2);

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 270" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={handleEnd} onMouseLeave={handleEnd} onTouchMove={onMove} onTouchEnd={handleEnd}>
        <text x="200" y="16" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag either point</text>
        {[0,1,2,3,4,5,6,7,8].map(i => <line key={`v${i}`} x1={80+i*30} y1="25" x2={80+i*30} y2="210" stroke="#f1f5f9" strokeWidth="1"/>)}
        {[0,1,2,3,4,5,6,7].map(i => <line key={`h${i}`} x1="70" y1={200-i*30} x2="330" y2={200-i*30} stroke="#f1f5f9" strokeWidth="1"/>)}
        <line x1="70" y1="200" x2="330" y2="200" stroke={C.textMid} strokeWidth="1.5"/>
        <line x1="80" y1="210" x2="80" y2="25" stroke={C.textMid} strokeWidth="1.5"/>
        {/* Right triangle */}
        <line x1={s1x} y1={s1y} x2={s2x} y2={s1y} stroke={C.primaryLight} strokeWidth="1.5" strokeDasharray="5,3"/>
        <line x1={s2x} y1={s1y} x2={s2x} y2={s2y} stroke={C.primaryLight} strokeWidth="1.5" strokeDasharray="5,3"/>
        {/* Distance line */}
        <line x1={s1x} y1={s1y} x2={s2x} y2={s2y} stroke="#E24B4A" strokeWidth="2.5"/>
        {/* Points */}
        <circle cx={s1x} cy={s1y} r="10" fill={C.primaryMid} stroke="white" strokeWidth="2" style={{cursor:"grab"}}
          onMouseDown={onStartP1} onTouchStart={onStartP1}/>
        <text x={s1x-14} y={s1y-12} fontSize="11" fill={C.primaryMid} fontFamily={ff} fontWeight="500">A({p1.x},{p1.y})</text>
        <circle cx={s2x} cy={s2y} r="10" fill={C.amberDark} stroke="white" strokeWidth="2" style={{cursor:"grab"}}
          onMouseDown={onStartP2} onTouchStart={onStartP2}/>
        <text x={s2x+8} y={s2y-12} fontSize="11" fill={C.amberDark} fontFamily={ff} fontWeight="500">B({p2.x},{p2.y})</text>
        {/* Labels */}
        <text x={(s1x+s2x)/2} y={s1y+16} fontSize="10" fill={C.textMute} fontFamily={ff} textAnchor="middle">{Math.abs(dx)}</text>
        <text x={s2x+14} y={(s1y+s2y)/2+4} fontSize="10" fill={C.textMute} fontFamily={ff}>{Math.abs(dy)}</text>
        {/* Formula */}
        <rect x="60" y="225" width="280" height="38" rx="10" fill="#FAEEDA"/>
        <text x="200" y="243" textAnchor="middle" fontSize="12" fill={C.amberDark} fontFamily={ff} fontWeight="500">
          d = √({dx}² + {dy}²) = √{dx*dx + dy*dy} = {dist}
        </text>
        <text x="200" y="258" textAnchor="middle" fontSize="10" fill={C.textMute} fontFamily={ff}>d = √[(x₂−x₁)² + (y₂−y₁)²]</text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  9. OHM'S LAW
// ═══════════════════════════════════════

export function InteractiveOhmsLaw() {
  const [voltage, setVoltage] = useState(12);
  const [resistance, setResistance] = useState(4);
  const current = (voltage / resistance).toFixed(2);

  return (
    <div>
      <svg viewBox="0 0 400 280" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto"}}>
        <text x="200" y="18" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Adjust voltage and resistance</text>
        {/* Circuit */}
        <rect x="80" y="60" width="240" height="120" rx="12" fill="none" stroke={C.textMid} strokeWidth="2"/>
        {/* Battery */}
        <line x1="160" y1="60" x2="160" y2="45" stroke={C.textMid} strokeWidth="2"/>
        <line x1="145" y1="45" x2="175" y2="45" stroke={C.amberDark} strokeWidth="3"/>
        <line x1="150" y1="38" x2="170" y2="38" stroke={C.amberDark} strokeWidth="2"/>
        <text x="160" y="33" textAnchor="middle" fontSize="10" fill={C.amberDark} fontFamily={ff} fontWeight="500">{voltage}V</text>
        {/* Resistor */}
        <path d="M 260 180 L 270 170 L 280 190 L 290 170 L 300 190 L 310 170 L 320 180" fill="none" stroke={C.purpleMid} strokeWidth="2" transform="translate(0,-120)"/>
        <text x="290" y="48" textAnchor="middle" fontSize="10" fill={C.purpleMid} fontFamily={ff} fontWeight="500">{resistance}Ω</text>
        {/* Current arrow */}
        <polygon points="200,65 210,55 210,59 240,59 240,71 210,71 210,75" fill={C.primaryMid} opacity="0.6"/>
        <text x="225" y="84" textAnchor="middle" fontSize="10" fill={C.primaryMid} fontFamily={ff} fontWeight="500">I = {current}A</text>
        {/* Electron flow dots */}
        <circle cx="110" cy="120" r="3" fill={C.primaryLight}/>
        <circle cx="150" cy="180" r="3" fill={C.primaryLight}/>
        <circle cx="250" cy="180" r="3" fill={C.primaryLight}/>
        <circle cx="300" cy="120" r="3" fill={C.primaryLight}/>
      </svg>
      <div style={{display:"flex",gap:20,justifyContent:"center",padding:"0 20px"}}>
        <div style={{flex:1,maxWidth:160}}>
          <div style={{fontSize:12,color:C.textMute,marginBottom:4,fontFamily:ff}}>Voltage (V)</div>
          <input type="range" min="1" max="24" value={voltage} step="1" onChange={e=>setVoltage(+e.target.value)}
            style={{width:"100%",accentColor:C.amberDark}}/>
          <div style={{fontSize:18,fontWeight:500,color:C.amberDark,fontFamily:ff,textAlign:"center"}}>{voltage}V</div>
        </div>
        <div style={{flex:1,maxWidth:160}}>
          <div style={{fontSize:12,color:C.textMute,marginBottom:4,fontFamily:ff}}>Resistance (Ω)</div>
          <input type="range" min="1" max="20" value={resistance} step="1" onChange={e=>setResistance(+e.target.value)}
            style={{width:"100%",accentColor:C.purpleMid}}/>
          <div style={{fontSize:18,fontWeight:500,color:C.purpleMid,fontFamily:ff,textAlign:"center"}}>{resistance}Ω</div>
        </div>
      </div>
      <div style={{textAlign:"center",marginTop:12,padding:"10px 20px",background:"#E1F5EE",borderRadius:12,maxWidth:300,margin:"12px auto 0"}}>
        <span style={{fontSize:16,fontWeight:500,color:C.primaryMid,fontFamily:ff}}>V = IR → {voltage} = {current} × {resistance}</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
//  10. BPT (Basic Proportionality)
// ═══════════════════════════════════════

export function InteractiveBPT() {
  const svgRef = useRef(null);
  const [t, setT] = useState(0.5);

  const handleDrag = useCallback(({y}) => {
    setT(clamp((y - 50) / 150, 0.15, 0.85));
  }, []);

  const { onStart, onMove, onEnd } = useDrag(svgRef, 400, 270, handleDrag);
  const ax = 200, ay = 40;
  const bx = 80, by = 200;
  const ccx = 320, ccy = 200;
  const dx = ax + (bx - ax) * t;
  const dy = ay + (by - ay) * t;
  const ex = ax + (ccx - ax) * t;
  const ey = ay + (ccy - ay) * t;
  const ad = t.toFixed(2);
  const db = (1 - t).toFixed(2);
  const ae = t.toFixed(2);
  const ec = (1 - t).toFixed(2);

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 400 270" style={{width:"100%",maxWidth:420,display:"block",margin:"0 auto",touchAction:"none"}}
        onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd} onTouchMove={onMove} onTouchEnd={onEnd}>
        <text x="200" y="18" textAnchor="middle" fontSize="12" fill={C.textMute} fontFamily={ff}>Drag the line DE up or down</text>
        <polygon points={`${ax},${ay} ${bx},${by} ${ccx},${ccy}`} fill="none" stroke={C.textMid} strokeWidth="1.5"/>
        <text x={ax} y={ay-8} textAnchor="middle" fontSize="12" fill={C.text} fontFamily={ff} fontWeight="500">A</text>
        <text x={bx-12} y={by+4} fontSize="12" fill={C.text} fontFamily={ff} fontWeight="500">B</text>
        <text x={ccx+12} y={ccy+4} fontSize="12" fill={C.text} fontFamily={ff} fontWeight="500">C</text>
        <line x1={dx} y1={dy} x2={ex} y2={ey} stroke={C.primaryMid} strokeWidth="2.5"/>
        <circle cx={dx} cy={dy} r="6" fill={C.primaryMid} stroke="white" strokeWidth="2"/>
        <circle cx={ex} cy={ey} r="6" fill={C.primaryMid} stroke="white" strokeWidth="2"/>
        <circle cx={(dx+ex)/2} cy={(dy+ey)/2} r="10" fill={C.primaryMid} opacity="0" stroke="none" style={{cursor:"ns-resize"}}
          onMouseDown={onStart} onTouchStart={onStart}/>
        <text x={dx-14} y={dy} fontSize="11" fill={C.primaryMid} fontFamily={ff} fontWeight="500">D</text>
        <text x={ex+8} y={ey} fontSize="11" fill={C.primaryMid} fontFamily={ff} fontWeight="500">E</text>
        <text x={(ax+dx)/2-18} y={(ay+dy)/2} fontSize="10" fill={C.amberDark} fontFamily={ff} fontWeight="500">AD={ad}</text>
        <text x={(dx+bx)/2-18} y={(dy+by)/2} fontSize="10" fill={C.purpleMid} fontFamily={ff} fontWeight="500">DB={db}</text>
        <text x={(ax+ex)/2+10} y={(ay+ey)/2} fontSize="10" fill={C.amberDark} fontFamily={ff} fontWeight="500">AE={ae}</text>
        <text x={(ex+ccx)/2+10} y={(ey+ccy)/2} fontSize="10" fill={C.purpleMid} fontFamily={ff} fontWeight="500">EC={ec}</text>
        <text x={(dx+ex)/2} y={(dy+ey)/2-12} textAnchor="middle" fontSize="10" fill={C.primaryMid} fontFamily={ff}>DE ∥ BC</text>
        <rect x="80" y="230" width="240" height="32" rx="10" fill="#E1F5EE"/>
        <text x="200" y="248" textAnchor="middle" fontSize="12" fill={C.primaryMid} fontFamily={ff} fontWeight="500">
          AD/DB = {(t/(1-t)).toFixed(2)} = AE/EC = {(t/(1-t)).toFixed(2)} ✓
        </text>
        <text x="200" y="262" textAnchor="middle" fontSize="10" fill={C.textMute} fontFamily={ff}>AD/DB = AE/EC (BPT holds)</text>
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════
//  EXPORT MAP
// ═══════════════════════════════════════

export const INTERACTIVE_MAP = {
  "interactive-angle": InteractiveAngleExplorer,
  "interactive-pythagoras": InteractivePythagoras,
  "interactive-trig": InteractiveTrigRatios,
  "interactive-reflection": InteractiveReflection,
  "interactive-refraction": InteractiveRefraction,
  "interactive-tangent": InteractiveCircleTangent,
  "interactive-coordinate": InteractiveCoordinatePlane,
  "interactive-distance": InteractiveDistanceFormula,
  "interactive-ohms-law": InteractiveOhmsLaw,
  "interactive-bpt": InteractiveBPT,
};
